import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSidebar } from "../../content/SidebarContent";
import PageHeader from "../../components/PageHeader";
import { routes } from "../../helpers/constants";
import Loader from "../../components/Loader";
import { reqToAddCoupon } from "../../redux/slices.js/promotioncoupons/couponSlice";
import DatePicker from "react-datepicker";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Coupon Name is required!"),
  description: Yup.string().required("Description is required!"),
  discount_in_percentage: Yup.number()
    .min(1, "Minimum 1%")
    .max(100, "Maximum 100%")
    .required("Discount is required!"),
  start_at: Yup.date().required("Start date is required!"),
  end_at: Yup.date()
    .min(Yup.ref("start_at"), "End date must be after start date")
    .required("End date is required!"),
  usage_limit: Yup.number().when("hasUsageLimit", {
    is: true,
    then: (schema) =>
      schema.min(1, "Must be at least 1").required("Usage limit is required!"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const AddCoupons = () => {
  const { setIsSidebarOpen } = useSidebar();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loader = useSelector((state) => state.promotionCoupons.loader);

  const initialValues = {
    name: "",
    description: "",
    discount_in_percentage: "",
    start_at: "",
    end_at: "",
    hasUsageLimit: false, // checkbox controller
    usage_limit: "",
  };

  const handleSubmit = (values, { resetForm }) => {
    dispatch(
      reqToAddCoupon({
        data: {
          name: values.name,
          description: values.description,
          discount_in_percentage: Number(values.discount_in_percentage),
          start_at: values.start_at,
          end_at: values.end_at,
          usage_limit: values.hasUsageLimit ? Number(values.usage_limit) : null,
        },
        onSuccess: () => {
          resetForm();
          navigate(routes.promotionCoupons);
        },
        onFailure: () => {},
      })
    );
  };

  return (
    <>
      {loader && <Loader />}
      <section className="dashboard-page main-layout-page main-layout h-[calc(100vh-50px)]">
        <div className="!p-0">
          <div className="main-layout-wrapper bg-white h-screen flex flex-col sticky z-1 top-0">
            <PageHeader
              title="Add Coupon"
              description="Create a new promotion coupon."
              onOpenSidebar={() => setIsSidebarOpen(true)}
            />

            <div className="flex items-baseline gap-[40px] main-sidebar-wrapper flex-1">
              <div className="flex-1 right-sidebar h-full relative">
                <div className="right-sidebar-contend py-6 px-3 md:px-6 pb-[50px] h-full">
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ values, setFieldValue }) => (
                      <Form className="signin-form flex flex-col gap-6">
                        <div className="bg-white box-shadow p-5 space-y-7 rounded-md">
                          <h2 className="text-h4 text-blue">
                            Coupon Information
                          </h2>

                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <label htmlFor="name">
                                Coupon Name <span>*</span>
                              </label>
                              <Field
                                name="name"
                                placeholder="Enter coupon name"
                                className="w-full"
                              />
                              <ErrorMessage
                                name="name"
                                component="div"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>

                            <div>
                              <label htmlFor="description">
                                Description <span>*</span>
                              </label>
                              <Field
                                name="description"
                                placeholder="Enter description"
                                className="w-full"
                              />
                              <ErrorMessage
                                name="description"
                                component="div"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>

                            <div>
                              <label htmlFor="discount_in_percentage">
                                Discount (%) <span>*</span>
                              </label>
                              <Field
                                name="discount_in_percentage"
                                type="number"
                                placeholder="Enter discount %"
                                className="w-full"
                              />
                              <ErrorMessage
                                name="discount_in_percentage"
                                component="div"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>

                            <div>
                              <label htmlFor="start_at">
                                Start Date <span>*</span>
                              </label>
                              {/* <Field
                                name="start_at"
                                type="date"
                                className="w-full"
                                min={new Date().toISOString().split("T")[0]} 
                              /> */}
                               <DatePicker
    selected={values.start_at}
    onChange={(date) => setFieldValue("start_at", date)}
    minDate={new Date()} // 👈 restrict past dates
    dateFormat="yyyy-MM-dd"
    className="w-full border rounded px-2 py-1"
    placeholderText="Select Start Date "
  />
                              <ErrorMessage
                                name="start_at"
                                component="div"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>



                            <div>
                              <label htmlFor="end_at">
                                End Date <span>*</span>
                              </label>
                              {/* <Field
                                name="end_at"
                                type="date"
                                className="w-full"
                                min={new Date().toISOString().split("T")[0]}
                              /> */}
                               <DatePicker
    selected={values.end_at}
    onChange={(date) => setFieldValue("end_at", date)}
    minDate={values.start_at || new Date()} // 👈 end must be >= start
    dateFormat="yyyy-MM-dd"
    className="w-full border rounded px-2 py-1"
    placeholderText="Select End Date "
  />
                              <ErrorMessage
                                name="end_at"
                                component="div"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>

                            {/* Checkbox + conditional usage limit */}
                            <div className="col-span-2">
                              <label className="!flex items-center gap-2 justify-start">
                                <Field
                                  type="checkbox"
                                  name="hasUsageLimit"
                                  className="!w-fit"
                                  checked={values.hasUsageLimit}
                                  onChange={(e) => {
                                    setFieldValue(
                                      "hasUsageLimit",
                                      e.target.checked
                                    );
                                    if (!e.target.checked)
                                      setFieldValue("usage_limit", "");
                                  }}
                                />
                                Enable Usage Limit
                              </label>

                              {values.hasUsageLimit && (
                                <div className="mt-2">
                                  <Field
                                    name="usage_limit"
                                    type="number"
                                    placeholder="Enter usage limit"
                                    className="w-full"
                                  />
                                  <ErrorMessage
                                    name="usage_limit"
                                    component="div"
                                    className="text-red-500 text-sm mt-1"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-4 mt-auto">
                          <button
                            type="submit"
                            className="login-btn text-white"
                          >
                            Submit
                          </button>
                          <button
                            type="button"
                            className="skip-btn border-blue"
                            onClick={() => navigate(-1)}
                          >
                            Back
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AddCoupons;
